import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import {
  Heart, Shield, Users, Pill, Activity, Building2,
  ChevronDown, Star, Globe, Clock, Check,
  ArrowRight, Menu, X, Bell,
  Mail, Send, Loader2, CheckCircle, Lock,
  FileText, TrendingUp, Tablet, Upload, Trash2
} from 'lucide-react';
import { NsofarLogo } from '@/components/NsofarLogo';
import DarkModeToggle from '@/components/DarkModeToggle';
import { trackEvent } from '@/lib/analytics';
import { openCookieSettings } from '@/lib/consent';
import FAQ_DATA from '@/data/landingFaq.json';

// Served from public/landing/.
const HERO_CAROUSEL = [
  { src: '/landing/hero-india.jpg', alt: 'Indian multigenerational family', label: 'India' },
  { src: '/landing/hero-east-asia.jpg', alt: 'East Asian multigenerational family', label: 'East Asia' },
  { src: '/landing/hero-europe.jpg', alt: 'European multigenerational family', label: 'Europe' },
  { src: '/landing/hero-africa.jpg', alt: 'African multigenerational family', label: 'Africa' },
  { src: '/landing/hero-southeast-asia.jpg', alt: 'Southeast Asian multigenerational family', label: 'Southeast Asia' },
];

import { getApiUrl } from '../lib/apiUrl';
import { useShowPricing } from '@/hooks/useShowPricing';
const API = getApiUrl();

/* ====== P9: NAVBAR ====== */
function Navbar({ onNavigate, showPricing }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const scrollTo = (id) => { setMobileOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  const links = [
    { label: 'Features', action: () => scrollTo('features') },
    { label: 'How it works', action: () => scrollTo('how-it-works') },
    ...(showPricing ? [{ label: 'Pricing', action: () => scrollTo('pricing') }] : []),
    { label: 'About', to: '/about' },
    { label: 'Blog', to: '/blog' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ background: scrolled ? 'color-mix(in srgb, var(--surface) 96%, transparent)' : 'transparent', backdropFilter: scrolled ? 'blur(16px)' : 'none', borderBottom: '1px solid ' + (scrolled ? 'var(--border-color)' : 'transparent') }}
      data-testid="landing-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <button onClick={() => scrollTo('hero')} className="flex items-center gap-2" aria-label="nsofar home">
          <NsofarLogo size="sm" />
        </button>
        <div className="hidden md:flex items-center gap-8">
          {links.map(l => l.to ? (
            <Link key={l.label} to={l.to} className="inline-flex items-center text-sm font-medium transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>
              {l.label}
            </Link>
          ) : (
            <button key={l.label} onClick={l.action} className="text-sm font-medium transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <DarkModeToggle />
          <button onClick={() => onNavigate('/auth?role=user')} className="text-sm font-medium px-4 py-2 rounded-full transition-colors" style={{ color: 'var(--color-insurance)' }} data-testid="landing-signin-btn">
            Log in
          </button>
          <button onClick={() => { trackEvent('cta_click', { cta_label: 'get_started_free', cta_location: 'nav' }); onNavigate('/auth?role=user&mode=signup'); }} className="text-sm font-semibold px-5 py-2.5 rounded-full text-white transition-all hover:shadow-lg hover:-translate-y-0.5" style={{ background: 'var(--color-insurance)' }} data-testid="landing-get-started-btn">
            Get started free
          </button>
        </div>
        <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t px-4 py-4 space-y-2" style={{ background: 'var(--surface)', borderColor: 'var(--border-color)' }}>
          {links.map(l => l.to ? (
            <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)} className="block w-full text-left text-sm font-medium py-2.5 px-3 rounded-lg transition-colors hover:bg-[var(--hover-surface)]" style={{ color: 'var(--text-main)' }}>
              {l.label}
            </Link>
          ) : (
            <button key={l.label} onClick={() => { setMobileOpen(false); l.action(); }} className="block w-full text-left text-sm font-medium py-2.5 px-3 rounded-lg transition-colors hover:bg-[var(--hover-surface)]" style={{ color: 'var(--text-main)' }}>
              {l.label}
            </button>
          ))}
          <div className="pt-2 flex gap-2">
            <button onClick={() => { setMobileOpen(false); onNavigate('/auth?role=user'); }} className="flex-1 text-sm font-medium py-2.5 rounded-full border" style={{ color: 'var(--color-insurance)', borderColor: 'var(--border-color)' }}>
              Log in
            </button>
            <button onClick={() => { setMobileOpen(false); trackEvent('cta_click', { cta_label: 'get_started_free', cta_location: 'nav' }); onNavigate('/auth?role=user&mode=signup'); }} className="flex-1 text-sm font-semibold py-2.5 rounded-full text-white" style={{ background: 'var(--color-insurance)' }}>
              Get started free
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ====== P1: HERO SECTION ====== */
function Hero({ onNavigate }) {
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => { setCarouselIdx(i => (i + 1) % HERO_CAROUSEL.length); setFade(true); }, 400);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="hero" className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden" data-testid="landing-hero">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(13,148,136,0.06) 0%, transparent 70%)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
              Care for your parents in India from abroad.
            </h1>
            <p className="text-base sm:text-lg leading-relaxed mb-8 max-w-lg" style={{ color: 'var(--text-muted)' }}>
              Book doctor consults, lab tests at home and medicine delivery for your parents, and get their reports explained in plain words.
            </p>
            {/* Single primary CTA (P18). Secondary "See how it works"
                anchor-scroll dropped in Session 13 / B6 — the chevron
                + below-fold sections already invite the scroll. */}
            <div className="mb-6">
              <button onClick={() => { trackEvent('cta_click', { cta_label: 'get_started_free', cta_location: 'hero' }); onNavigate('/auth?role=user&mode=signup'); }}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white transition-all hover:shadow-xl hover:-translate-y-0.5"
                style={{ background: 'var(--color-insurance)' }} data-testid="hero-cta-primary">
                Get started free <ArrowRight size={16} />
              </button>
            </div>
            {/* Trust bar */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" style={{ color: 'var(--text-light)' }}>
              <span className="flex items-center gap-1.5"><Lock size={12} style={{ color: 'var(--color-insurance)' }} /> AES-256-GCM Encrypted</span>
              <span className="flex items-center gap-1.5"><Check size={12} style={{ color: 'var(--color-insurance)' }} /> DPDP & GDPR Compliant</span>
              <span className="flex items-center gap-1.5"><Upload size={12} style={{ color: 'var(--color-insurance)' }} /> Export or delete anytime</span>
            </div>
          </div>
          {/* Right — Carousel */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ border: '4px solid white' }}>
              <img src={HERO_CAROUSEL[carouselIdx].src} alt={HERO_CAROUSEL[carouselIdx].alt}
                className="w-full object-cover object-top" loading="eager"
                style={{ transition: 'opacity 0.4s ease', opacity: fade ? 1 : 0, height: 'clamp(260px, 50vw, 480px)' }}
                data-testid="hero-carousel-image" />
              <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent)' }} />
              {/* From lg up the two floating cards below cover the photo's
                  bottom 52px, so the label and dots sit just above them. */}
              <div className="absolute bottom-4 lg:bottom-16 left-4 right-4 flex items-center justify-between" style={{ zIndex: 10 }}>
                <span className="text-xs font-medium text-white/90 px-3 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                  {HERO_CAROUSEL[carouselIdx].label}
                </span>
                {/* index.css gives every button a minimum height for touch
                    (32px, 40px on phones), so the button is only the
                    transparent tap area and the 6px pill is drawn inside it.
                    The 3px side padding makes neighbouring tap areas meet
                    (6px between pills); h-1.5 stops the tall tap areas from
                    pushing the label up. */}
                <div className="flex items-center h-1.5 -mx-[3px]">
                  {HERO_CAROUSEL.map((slide, i) => (
                    <button key={i} onClick={() => { setFade(false); setTimeout(() => { setCarouselIdx(i); setFade(true); }, 300); }}
                      aria-label={`Show ${slide.label} photo`} aria-current={i === carouselIdx ? 'true' : undefined}
                      data-testid={`hero-carousel-dot-${i}`} className="flex items-center px-[3px]">
                      <span style={{ width: i === carouselIdx ? 20 : 6, height: 6, borderRadius: 3, background: i === carouselIdx ? 'white' : 'rgba(255,255,255,0.4)', transition: 'all 0.3s ease' }} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -left-8 bg-white rounded-2xl p-4 shadow-lg items-center gap-3 animate-fade-in-up hidden lg:flex" style={{ animationDelay: '0.3s' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#E8F5E9' }}><Shield size={18} style={{ color: '#27AE60' }} /></div>
              <div><p className="text-sm font-semibold" style={{ color: 'var(--marketing-strong)' }}>Always Protected</p><p className="text-xs" style={{ color: 'var(--text-light)' }}>24/7 health monitoring</p></div>
            </div>
            <div className="absolute -bottom-4 right-8 bg-white rounded-2xl p-4 shadow-lg items-center gap-3 animate-fade-in-up hidden lg:flex" style={{ animationDelay: '0.5s' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#FFF3E0' }}><Bell size={18} style={{ color: '#F39C12' }} /></div>
              <div><p className="text-sm font-semibold" style={{ color: 'var(--marketing-strong)' }}>Medication Reminder</p><p className="text-xs" style={{ color: 'var(--text-light)' }}>Never miss a dose</p></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====== P2: STATS / BENEFIT TILES ====== */
function BenefitTiles() {
  const tiles = [
    { icon: Globe, heading: 'Manage from anywhere', body: 'Sydney, Dubai, London, Toronto \u2014 it doesn\u2019t matter where you are.' },
    { icon: Clock, heading: 'Built for your timezone', body: 'No more 2 AM calls. No more coordinating through relatives.' },
    { icon: Lock, heading: 'Your data, your control', body: 'AES-256-GCM encrypted. Export or delete anytime.' },
    { icon: Heart, heading: 'Free to start', body: 'No credit card required. Set up your first family member in minutes.' },
  ];
  return (
    <section className="py-12 md:py-16" style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }} data-testid="landing-benefit-tiles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {tiles.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} className="text-center sm:text-left" data-testid={`benefit-tile-${i}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto sm:mx-0 mb-3" style={{ background: 'var(--brand-primary-100)' }}>
                  <Icon size={20} style={{ color: 'var(--color-insurance)' }} />
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--marketing-strong)' }}>{t.heading}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{t.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ====== P3: FEATURES ====== */
const FEATURES = [
  { icon: Pill, title: 'Smart Medication Manager', badge: 'Live now', badgeColor: '#16A34A', desc: 'Track every medication, set AI-powered reminders, and get alerts when a dose is missed \u2014 so your parents never fall behind on treatment.', color: '#27AE60', bg: '#E8F5E9' },
  { icon: FileText, title: 'Health Report Analysis', badge: 'Live now', badgeColor: '#16A34A', desc: 'Upload any health report or test result. Our AI reads it and gives you a plain-language summary \u2014 no medical degree required.', color: 'var(--color-insurance)', bg: 'var(--brand-primary-100)' },
  { icon: Activity, title: 'Prescription Reader', badge: 'Live now', badgeColor: '#16A34A', desc: 'Photograph a handwritten prescription. nsofar extracts every medicine, dosage, and instruction automatically.', color: 'var(--color-consultation)', bg: '#F3E8FF' },
  { icon: TrendingUp, title: 'Health Trends', badge: 'Live now', badgeColor: '#16A34A', desc: 'See how your loved one\u2019s health is tracking over time. Spot patterns early, before they become problems.', color: '#0891B2', bg: '#ECFEFF' },
  { icon: Tablet, title: 'Elderly Portal', badge: 'Live now', badgeColor: '#16A34A', desc: 'A simplified interface your parents can actually use. Large text, minimal steps, their own window into their health.', color: '#F39C12', bg: '#FFF3E0' },
  { icon: Building2, title: 'PropertyGuard', badge: 'Coming soon', badgeColor: 'var(--text-light)', desc: 'Rent collection, tenant management, maintenance coordination, and property documentation \u2014 all managed remotely from your dashboard.', color: '#E74C3C', bg: '#FDECEA' },
];

function Features() {
  return (
    <section id="features" className="py-16 md:py-24" data-testid="landing-features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
            Everything back home, in one place.
          </h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: 'var(--text-muted)' }}>
            What's live today — with more coming.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="group p-6 md:p-8 rounded-2xl border transition-all duration-400 hover:shadow-xl hover:-translate-y-1.5"
                style={{ background: 'var(--surface)', borderColor: 'var(--marketing-soft-border)' }} data-testid={`feature-card-${i}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{ background: f.bg }}>
                    <Icon size={22} style={{ color: f.color }} />
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: f.badgeColor }}>{f.badge}</span>
                </div>
                <h3 className="text-base sm:text-lg font-bold mb-2.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ====== HOW IT WORKS ====== */
const STEPS = [
  { step: '01', title: 'Create your family profile', desc: 'Sign up and add your elderly family members \u2014 their health records, medications, and emergency contacts.', icon: Heart },
  { step: '02', title: 'Upload reports & prescriptions', desc: 'Photograph prescriptions or upload test results. AI extracts everything and gives you plain-language summaries.', icon: FileText },
  { step: '03', title: 'Monitor from anywhere', desc: 'Track health vitals, medication adherence, and trends \u2014 all from your phone, in any timezone.', icon: Globe },
  { step: '04', title: 'Stop worrying. Start knowing.', desc: 'AI alerts you to health risks, medication conflicts, and missed doses before they become emergencies.', icon: Shield },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-16 md:py-24" style={{ background: 'var(--bg-main)' }} data-testid="landing-how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--color-insurance)' }}>HOW IT WORKS</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
            Set up in minutes, care for years
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="relative p-6 rounded-2xl bg-white border transition-all hover:shadow-md" style={{ borderColor: 'var(--marketing-soft-border)' }}>
                <span className="text-5xl font-bold absolute top-4 right-4" style={{ color: 'var(--marketing-soft-border)', fontFamily: 'Outfit, sans-serif' }}>{s.step}</span>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--brand-primary-100)' }}>
                  <Icon size={20} style={{ color: 'var(--color-insurance)' }} />
                </div>
                <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ====== P6: PRICING (teaser \u2014 Session 13 / B6) ======
   Was 4-card full pricing table that duplicated /plans content. Now a
   single compact teaser linking to /plans where the canonical pricing
   detail lives. Drops ~55 LoC; keeps the section on-page so visitors
   who scroll for price find a clear hook + path. */
function Pricing({ onNavigate }) {
  return (
    <section id="pricing" className="py-16 md:py-24" data-testid="landing-pricing">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
          Simple, transparent pricing.
        </h2>
        <p className="text-base mb-8" style={{ color: 'var(--text-muted)' }}>
          Four plans, starting free. No surprises, cancel anytime.
        </p>
        <div
          className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 px-6 py-5 rounded-2xl border bg-white"
          style={{ borderColor: 'var(--marketing-soft-border)' }}
          data-testid="pricing-teaser-card"
        >
          <div className="text-left">
            <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--color-insurance)' }}>
              From free to $99/mo
            </p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Compare all four tiers + every inclusion side-by-side on the plans page.
            </p>
          </div>
          <button
            onClick={() => { trackEvent('cta_click', { cta_label: 'see_plans', cta_location: 'pricing_teaser' }); onNavigate('/plans'); }}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap"
            style={{ background: 'var(--color-insurance)' }}
            data-testid="pricing-teaser-cta"
          >
            See all plans <ArrowRight size={14} />
          </button>
        </div>
        <p className="text-xs mt-6" style={{ color: 'var(--text-light)' }}>
          All plans include: AES-256-GCM encryption &middot; DPDP &amp; GDPR compliant &middot; Export or delete anytime
        </p>
      </div>
    </section>
  );
}

/* ====== P4: TESTIMONIALS ====== */
const TESTIMONIALS = [
  { name: 'Ananya R.', location: 'London, UK', text: 'I used to spend every Sunday morning calling relatives to check on my mother in Chennai. Now I open nsofar and I can see everything \u2014 her medications, her reports, her trends. That anxiety is gone.' },
  { name: 'Raj M.', location: 'Dubai, UAE', text: 'My father was on six medications and nobody was tracking whether he was actually taking them. nsofar\u2019s reminders changed that. The first month, it caught three missed doses we would never have known about.' },
  { name: 'Priya S.', location: 'Sydney, Australia', text: 'Uploading my mother\u2019s blood test reports and getting a clear summary within seconds \u2014 that feature alone is worth everything. I finally understand what her doctor is telling her.' },
];

function Testimonials() {
  return (
    <section className="py-16 md:py-24" style={{ background: 'var(--bg-main)' }} data-testid="landing-testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
            They used to worry. Now they don't.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="p-6 md:p-8 rounded-2xl bg-white border transition-all hover:shadow-md" style={{ borderColor: 'var(--marketing-soft-border)' }} data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} fill="#F39C12" color="#F39C12" />)}
              </div>
              <p className="text-sm leading-relaxed mb-6" style={{ color: '#334155' }}>"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{ background: ['var(--color-insurance)', 'var(--color-insurance)', '#F39C12'][i] }}>
                  {t.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--marketing-strong)' }}>{t.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-light)' }}>{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--text-light)' }}>
          Names and identifying details have been changed for privacy. Experiences are representative of real customer feedback.
        </p>
      </div>
    </section>
  );
}

/* ====== P7: PRIVACY & TRUST ====== */
function PrivacyTrust() {
  const cards = [
    { icon: Lock, heading: 'End-to-End Encryption', body: 'AES-256-GCM encryption on all data, at rest and in transit.' },
    { icon: Shield, heading: 'DPDP & GDPR Compliant', body: 'Fully compliant with India\u2019s DPDPA 2023 and the EU/UK GDPR.' },
    { icon: Upload, heading: 'Export Anytime', body: 'Download a complete copy of your data at any time, in any format.' },
    { icon: Trash2, heading: 'Delete Anytime', body: 'Permanently delete your account and all data. No retention. No exceptions.' },
  ];
  return (
    <section className="py-16 md:py-24" data-testid="landing-privacy">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
          Your privacy is our promise.
        </h2>
        <p className="text-sm font-medium mb-6" style={{ color: 'var(--text-muted)' }}>
          Not a footnote. Not fine print. A commitment.
        </p>
        <p className="text-sm leading-relaxed max-w-2xl mx-auto mb-12" style={{ color: 'var(--text-muted)' }}>
          You're trusting nsofar with your family's most sensitive health data. We don't take that lightly. Every piece of data on nsofar is encrypted with AES-256-GCM — the same standard used by banks and defence agencies. You have full control: export or delete your data at any time, no questions asked, no exceptions.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="p-5 rounded-2xl bg-white border text-center transition-all hover:shadow-md" style={{ borderColor: 'var(--marketing-soft-border)' }} data-testid={`privacy-card-${i}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--brand-primary-100)' }}>
                  <Icon size={20} style={{ color: 'var(--color-insurance)' }} />
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--marketing-strong)' }}>{c.heading}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{c.body}</p>
              </div>
            );
          })}
        </div>
        <a href="/privacy" className="inline-block text-sm font-medium mt-8 transition-colors hover:underline" style={{ color: 'var(--color-insurance)' }}>
          Read our full Privacy Policy &rarr;
        </a>
      </div>
    </section>
  );
}

/* ====== FAQ ======
   The questions live in src/data/landingFaq.json so the build-time SEO
   step (scripts/seo-prerender.js) can emit FAQPage markup from the very
   text shown here. */

/* ====== FAQ + CONTACT (merged — Session 13 / B6) ======
   Was two adjacent sections (Common questions + Let's talk). Merged so
   the user lands on one "Have questions?" surface that answers in line
   (FAQ accordion on lg+ left) AND invites contact (form on lg+ right).
   On mobile they stack — FAQ first, contact below. */
function FAQContact() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="faq" className="py-16 md:py-24" style={{ background: 'var(--bg-main)' }} data-testid="landing-faq-contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>
            Have questions?
          </h2>
          <p className="text-base" style={{ color: 'var(--text-muted)' }}>
            Answers below. Don't see yours? Drop us a note.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* FAQ accordion — left 3/5 on lg+, full-width on mobile */}
          <div className="lg:col-span-3 space-y-3" data-testid="landing-faq">
            {FAQ_DATA.map((faq, i) => (
              <div key={i} className="rounded-2xl border transition-all overflow-hidden" style={{ background: 'var(--surface)', borderColor: openIdx === i ? 'var(--color-insurance)' : 'var(--marketing-soft-border)' }} data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" aria-expanded={openIdx === i}>
                  <span className="text-sm font-semibold pr-4" style={{ color: 'var(--marketing-strong)' }}>{faq.q}</span>
                  <ChevronDown size={18} className="flex-shrink-0 transition-transform" style={{ color: 'var(--text-light)', transform: openIdx === i ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                <div className="transition-all duration-300 overflow-hidden" style={{ maxHeight: openIdx === i ? '300px' : '0', opacity: openIdx === i ? 1 : 0 }}>
                  <p className="px-5 pb-5 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Contact form — right 2/5 on lg+, below FAQ on mobile */}
          <div className="lg:col-span-2">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====== CONTACT (form-only — section header lives in FAQContact) ====== */
function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', country: '', interest: '', message: '' });
  const [status, setStatus] = useState('idle');
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      trackEvent('contact_form_submitted', { interest: form.interest || 'not_specified' });
      setStatus('sent');
      setForm({ name: '', email: '', country: '', interest: '', message: '' });
    } catch { setStatus('error'); }
  };
  const interests = ['Elder Care', 'Property Management', 'Health Monitoring', 'Full Platform', 'Partnership / Business', 'Other'];
  // Each input's maxLength mirrors ContactSubmission in backend/models.py: text over a
  // server limit is answered 422 and the message is lost (tests/test_country_name_length.py).
  return (
    <div id="contact" data-testid="landing-contact">
      <div className="rounded-2xl p-6 md:p-8 border" style={{ background: 'var(--surface)', borderColor: 'var(--marketing-soft-border)' }}>
        <p className="text-sm font-semibold mb-1" style={{ color: 'var(--marketing-strong)', fontFamily: 'Outfit, sans-serif' }}>Don't see your question?</p>
        <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>Tell us about your situation — we respond within 24 hours.</p>
            {status === 'sent' ? (
              <div className="text-center py-8" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--brand-primary-100)' }}><CheckCircle size={32} style={{ color: 'var(--color-insurance)' }} /></div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--marketing-strong)' }}>Thank you!</h3>
                <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>We've received your message and will get back to you within 24 hours.</p>
                <button onClick={() => setStatus('idle')} className="text-sm font-medium px-5 py-2.5 rounded-full transition-colors" style={{ color: 'var(--color-insurance)', border: '1.5px solid var(--color-insurance)' }}>Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-xs font-medium mb-1.5" style={{ color: '#334155' }}>Name *</label><input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required maxLength={200} data-testid="contact-name" className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none transition-colors focus:border-[var(--color-insurance)]" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-main)' }} /></div>
                  <div><label className="block text-xs font-medium mb-1.5" style={{ color: '#334155' }}>Email *</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" required maxLength={254} data-testid="contact-email" className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none transition-colors focus:border-[var(--color-insurance)]" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-main)' }} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-xs font-medium mb-1.5" style={{ color: '#334155' }}>Country</label><input type="text" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="Where are you based?" maxLength={100} data-testid="contact-country" className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none transition-colors focus:border-[var(--color-insurance)]" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-main)' }} /></div>
                  <div><label className="block text-xs font-medium mb-1.5" style={{ color: '#334155' }}>I'm interested in</label><select value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })} data-testid="contact-interest" className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none transition-colors focus:border-[var(--color-insurance)] appearance-none" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-main)', color: form.interest ? 'var(--marketing-strong)' : 'var(--text-light)' }}><option value="">Select an option</option>{interests.map(i => <option key={i} value={i}>{i}</option>)}</select></div>
                </div>
                <div><label className="block text-xs font-medium mb-1.5" style={{ color: '#334155' }}>Message *</label><textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your situation..." required rows={4} maxLength={5000} data-testid="contact-message" className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none transition-colors focus:border-[var(--color-insurance)] resize-none" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-main)' }} /></div>
                {status === 'error' && <p className="text-xs font-medium" style={{ color: '#EF4444' }} data-testid="contact-error">Something went wrong. Please try again.</p>}
                <button type="submit" disabled={status === 'sending'} data-testid="contact-submit" className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: 'var(--color-insurance)' }}>
                  {status === 'sending' ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Message</>}
                </button>
                <p className="text-xs text-center" style={{ color: 'var(--text-light)' }}>We'll never share your information with third parties.</p>
              </form>
            )}
      </div>
    </div>
  );
}

/* ====== CTA BANNER ====== */
function CTABanner({ onNavigate }) {
  return (
    <section className="py-16 md:py-20" style={{ background: '#0F172A' }} data-testid="landing-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Your family deserves care, not worry
        </h2>
        <p className="text-base mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-light)' }}>
          Join diaspora families already managing life back home with nsofar.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => { trackEvent('cta_click', { cta_label: 'get_started_free', cta_location: 'cta_banner' }); onNavigate('/auth?role=user&mode=signup'); }} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold text-white transition-all hover:shadow-xl hover:-translate-y-0.5" style={{ background: 'var(--color-insurance)' }} data-testid="cta-get-started">
            Get started free <ArrowRight size={16} />
          </button>
          <button onClick={() => onNavigate('/auth?role=user')} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium border transition-all hover:bg-white/5" style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.25)' }}>
            Log in to your account
          </button>
        </div>
        <p className="mt-6 text-sm" style={{ color: 'var(--text-muted)' }}>
          Have 5 minutes?{' '}
          <a href="/feedback" className="font-semibold underline underline-offset-2 transition-colors hover:text-white" style={{ color: '#60A5FA' }} data-testid="cta-survey-link">
            Help us build nsofar right &rarr;
          </a>
        </p>
      </div>
    </section>
  );
}

/* ====== P10: FOOTER ====== */
// Footer and nav links are real <a href> (Link) so crawlers can follow them.
// index.css gives <a> and <button> a min-height touch target; a button centres
// its label in that box and an inline <a> ignores it, so the links carry
// inline-flex + items-center to sit exactly where the buttons beside them do.
function Footer({ showPricing }) {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <footer className="py-12" style={{ background: 'var(--bg-main)', borderTop: '1px solid var(--border-color)' }} data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {/* Column 1 - Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-3"><NsofarLogo size="sm" /></div>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--marketing-strong)' }}>Distance Disappears.</p>
            <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Life management for the global diaspora.</p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-light)' }}>For anyone living in one country with a life to manage in another.</p>
          </div>
          {/* Column 2 - Product */}
          <div>
            <h4 className="text-xs font-bold uppercase mb-3" style={{ color: 'var(--text-light)', letterSpacing: '0.05em' }}>Product</h4>
            <ul className="space-y-2">
              <li><button onClick={() => scrollTo('features')} className="text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Features</button></li>
              <li><button onClick={() => scrollTo('how-it-works')} className="text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>How it works</button></li>
              {showPricing && <li><button onClick={() => scrollTo('pricing')} className="text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Pricing</button></li>}
              <li><Link to="/portal" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Elderly Portal</Link></li>
              <li><button onClick={() => scrollTo('landing-privacy')} className="text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Security</button></li>
            </ul>
          </div>
          {/* Column 3 - Company */}
          <div>
            <h4 className="text-xs font-bold uppercase mb-3" style={{ color: 'var(--text-light)', letterSpacing: '0.05em' }}>Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>About</Link></li>
              <li><Link to="/blog" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Blog</Link></li>
              <li><Link to="/feedback" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }} data-testid="footer-survey-link">Share Feedback</Link></li>
              <li><span className="text-sm" style={{ color: 'var(--text-light)' }}>Careers</span></li>
              <li><button onClick={() => scrollTo('contact')} className="text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Contact</button></li>
            </ul>
          </div>
          {/* Column 4 - Legal */}
          <div>
            <h4 className="text-xs font-bold uppercase mb-3" style={{ color: 'var(--text-light)', letterSpacing: '0.05em' }}>Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Privacy Policy</Link></li>
              <li><Link to="/terms" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Terms & Conditions</Link></li>
              <li><Link to="/cookies" className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }}>Cookie Policy</Link></li>
              <li><button type="button" onClick={openCookieSettings} className="inline-flex items-center text-sm transition-colors hover:text-[var(--color-insurance)]" style={{ color: 'var(--text-muted)' }} data-testid="footer-cookie-settings">Cookie Settings</button></li>
            </ul>
          </div>
        </div>
        <div className="pt-6 border-t flex flex-col sm:flex-row justify-between items-center gap-3" style={{ borderColor: 'var(--border-color)' }}>
          <p className="text-xs" style={{ color: 'var(--text-light)' }}>&copy; {new Date().getFullYear()} nsofar, Inc. All rights reserved. Built for the global diaspora.</p>
          <p className="text-xs" style={{ color: 'var(--text-light)' }}>anoop@nsofar.com</p>
        </div>
      </div>
    </footer>
  );
}

/* ====== LANDING PAGE ====== */
export default function LandingPage() {
  const navigate = useNavigate();
  const onNavigate = (path) => navigate(path);
  const showPricing = useShowPricing();

  return (
    <main data-testid="landing-page">
      <SEOHead route="/" />
      <Navbar onNavigate={onNavigate} showPricing={showPricing} />
      <Hero onNavigate={onNavigate} />
      <BenefitTiles />
      <Features />
      <HowItWorks />
      {showPricing && <Pricing onNavigate={onNavigate} />}
      <Testimonials />
      <PrivacyTrust />
      <FAQContact />
      <CTABanner onNavigate={onNavigate} />
      <Footer showPricing={showPricing} />
    </main>
  );
}
